import {
  CSSProperties,
  FocusEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';
import {Body1, Body2} from '@ui/atoms/typography';
import {CancelIcon, ChevronDownIcon} from '@ui/atoms/icons';
import {AnimatePresence, motion} from 'framer-motion';
import {useTheme} from 'styled-components';
import {CheckInNameSection} from './check-in-name-section';
import {
  AudienceOption,
  BasicsStepProps,
  IncludeScope,
  IncludeScopeOption,
} from './types';

interface AudiencePickerProps {
  compact?: boolean;
  id: string;
  options: AudienceOption[];
  selected: AudienceOption[];
  placeholder: string;
  emptyLabel: string;
  isLoading?: boolean;
  groupByKind?: boolean;
  onChange: (options: AudienceOption[]) => void;
}

const optionKey = (option: AudienceOption) => `${option.kind}:${option.value}`;

const AudiencePicker = ({
  compact = false,
  id,
  options,
  selected,
  placeholder,
  emptyLabel,
  isLoading,
  groupByKind,
  onChange,
}: AudiencePickerProps) => {
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>();
  const selectedKeys = useMemo(() => new Set(selected.map(optionKey)), [
    selected,
  ]);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedQuery),
    );
  }, [options, query]);

  const toggleOption = (option: AudienceOption) => {
    const key = optionKey(option);
    onChange(
      selectedKeys.has(key)
        ? selected.filter((item) => optionKey(item) !== key)
        : [...selected, option],
    );
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (
      !wrapperRef.current?.contains(nextTarget) &&
      !menuRef.current?.contains(nextTarget)
    ) {
      setIsOpen(false);
    }
  };

  const updateMenuPosition = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    const viewportPadding = 8;
    const menuGap = 8;
    const maxMenuHeight = 240;
    const inputRect = input.getBoundingClientRect();
    const availableBelow =
      window.innerHeight - inputRect.bottom - menuGap - viewportPadding;
    const availableAbove = inputRect.top - menuGap - viewportPadding;
    const opensAbove = availableBelow < 160 && availableAbove > availableBelow;
    const availableHeight = opensAbove ? availableAbove : availableBelow;
    const maxHeight = Math.max(80, Math.min(maxMenuHeight, availableHeight));
    const width = Math.min(
      inputRect.width,
      window.innerWidth - viewportPadding * 2,
    );
    const left = Math.min(
      Math.max(inputRect.left, viewportPadding),
      window.innerWidth - width - viewportPadding,
    );

    setMenuStyle({
      position: 'fixed',
      left,
      width,
      maxHeight,
      zIndex: 10000,
      ...(opensAbove
        ? {bottom: window.innerHeight - inputRect.top + menuGap}
        : {top: inputRect.bottom + menuGap}),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsidePointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        !wrapperRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsidePointer);
    document.addEventListener('touchstart', handleOutsidePointer);

    return () => {
      document.removeEventListener('mousedown', handleOutsidePointer);
      document.removeEventListener('touchstart', handleOutsidePointer);
    };
  }, [isOpen]);

  const groups = groupByKind
    ? [
        {
          label: 'Groups and teams',
          options: filteredOptions.filter((option) => option.kind === 'group'),
        },
        {
          label: 'People',
          options: filteredOptions.filter((option) => option.kind === 'person'),
        },
      ]
    : [{label: '', options: filteredOptions}];

  return (
    <div ref={wrapperRef} onBlur={handleBlur} className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="search"
          value={query}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={`${id}-options`}
          aria-expanded={isOpen}
          placeholder={placeholder}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          onClick={() => setIsOpen(true)}
          onFocus={() => setIsOpen(true)}
          className={`${
            compact ? 'h-11 px-3 pr-10' : 'h-[52px] px-4 pr-12'
          } w-full rounded-[10px] border border-borderDark bg-white text-sm leading-[22px] text-blackTwo outline-none placeholder:text-textBody focus:border-purple300 focus:ring-1 focus:ring-purple300`}
        />
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}>
          <ChevronDownIcon color={theme.colors.blackTwo} />
        </span>
      </div>

      {isOpen &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            id={`${id}-options`}
            role="listbox"
            aria-multiselectable="true"
            style={menuStyle}
            className="overflow-y-auto rounded-[10px] border border-borderLight bg-white p-2 shadow-[0px_2px_5px_rgba(172,172,190,0.24)]">
            {isLoading ? (
              <Body2 kind="textBody" className="px-3 py-2">
                Loading options…
              </Body2>
            ) : filteredOptions.length === 0 ? (
              <Body2 kind="textBody" className="px-3 py-2">
                {emptyLabel}
              </Body2>
            ) : (
              groups.map((group) =>
                group.options.length > 0 ? (
                  <div key={group.label || 'options'}>
                    {group.label && (
                      <Body2
                        className="px-3 pb-1 pt-2 uppercase tracking-wide"
                        kind="textBody"
                        weight="semibold">
                        {group.label}
                      </Body2>
                    )}
                    {group.options.map((option) => {
                      const selectedOption = selectedKeys.has(
                        optionKey(option),
                      );
                      return (
                        <button
                          key={optionKey(option)}
                          type="button"
                          role="option"
                          aria-selected={selectedOption}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => toggleOption(option)}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-backgroundLight focus:outline-none focus:ring-2 focus:ring-purple200">
                          <SelectionCheckbox selected={selectedOption} />
                          <Body2 className="leading-[22px]" kind="blackTwo">
                            {option.label}
                          </Body2>
                        </button>
                      );
                    })}
                  </div>
                ) : null,
              )
            )}
          </div>,
          document.body,
        )}

      {selected.length > 0 && (
        <div
          className="mt-4 flex flex-wrap gap-2"
          aria-label="Selected options">
          {selected.map((option) => (
            <span
              key={optionKey(option)}
              className="flex h-9 items-center gap-2 rounded-lg border border-textBody bg-white px-3.5 py-2">
              <Body2 className="leading-5" kind="blackTwo" weight="semibold">
                {option.label}
              </Body2>
              <button
                type="button"
                aria-label={`Remove ${option.label}`}
                onClick={() => toggleOption(option)}
                className="rounded focus:outline-none focus:ring-2 focus:ring-purple200">
                <CancelIcon color={theme.colors.blackTwo} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

interface RecipientOptionProps {
  compact?: boolean;
  label: string;
  description: string;
  selected: boolean;
  selectionType: 'radio' | 'checkbox';
  name?: string;
  children?: ReactNode;
  onSelect: () => void;
}

const SelectionCheckmark = () => (
  <svg
    aria-hidden="true"
    data-testid="selection-checkmark"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 3.25L4.5 8.75L2 6.25"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SelectionCheckbox = ({
  selected,
  className,
  radio = false,
}: {
  selected: boolean;
  className?: string;
  radio?: boolean;
}) => (
  <motion.span
    animate={selected ? {scale: [1, 0.88, 1]} : {scale: 1}}
    transition={{duration: 0.18, ease: 'easeOut'}}
    aria-hidden="true"
    className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center border-2 transition-colors duration-150 ${
      radio ? 'rounded-full' : 'rounded-[5px] text-alwaysWhite'
    } ${className ?? ''} ${
      selected
        ? radio
          ? 'border-purple300 bg-white'
          : 'border-transparent bg-purple300'
        : 'border-borderDark bg-white'
    }`}>
    <AnimatePresence initial={false}>
      {selected && (
        <motion.span
          key={radio ? 'selected-dot' : 'selected-checkmark'}
          initial={{opacity: 0, scale: 0.4}}
          animate={{opacity: 1, scale: 1}}
          exit={{opacity: 0, scale: 0.4}}
          transition={{duration: 0.14, ease: 'easeOut'}}>
          {radio ? (
            <span className="block h-2 w-2 rounded-full bg-purple300" />
          ) : (
            <SelectionCheckmark />
          )}
        </motion.span>
      )}
    </AnimatePresence>
  </motion.span>
);

const RecipientOption = ({
  compact = false,
  label,
  description,
  selected,
  selectionType,
  name,
  children,
  onSelect,
}: RecipientOptionProps) => (
  <motion.div
    layout
    transition={{duration: 0.2, ease: 'easeOut'}}
    className={`rounded-[10px] border transition-colors duration-200 ${
      compact ? 'px-3 py-2.5' : 'px-4 py-3.5'
    } ${
      selected
        ? 'border-purple300 bg-backgroundLight'
        : 'border-borderDark bg-white'
    }`}>
    <label className="group flex w-full cursor-pointer items-center gap-4 text-left">
      <input
        type={selectionType}
        name={name}
        checked={selected}
        onChange={onSelect}
        className="peer sr-only"
      />
      <SelectionCheckbox
        selected={selected}
        radio={selectionType === 'radio'}
        className="peer-focus-visible:ring-2 peer-focus-visible:ring-purple200 peer-focus-visible:ring-offset-2"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Body2 className="leading-[22px]" kind="blackTwo" weight="semibold">
          {label}
        </Body2>
        <span className="text-[12px] leading-5 text-textBody">
          {description}
        </span>
      </span>
    </label>
    <AnimatePresence initial={false}>
      {selected && children ? (
        <motion.div
          key="recipient-options"
          data-testid="recipient-options"
          initial={{height: 0, marginTop: 0, opacity: 0}}
          animate={{height: 'auto', marginTop: compact ? 12 : 16, opacity: 1}}
          exit={{height: 0, marginTop: 0, opacity: 0}}
          transition={{duration: 0.2, ease: 'easeOut'}}
          className="overflow-visible">
          <div className="pl-[34px]">{children}</div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  </motion.div>
);

export const BasicsStep = ({
  compact = false,
  cycleName,
  includeScope,
  includeGroups,
  includePeople,
  excludeGroups,
  excludePeople,
  groupOptions,
  peopleOptions,
  isLoadingGroups,
  audienceError,
  onRetryAudience,
  onCycleNameChange,
  onIncludeScopeChange,
  onIncludeGroupsChange,
  onIncludePeopleChange,
  onExcludeScopeChange,
  onExcludeGroupsChange,
  onExcludePeopleChange,
}: BasicsStepProps) => {
  const [showExclusions, setShowExclusions] = useState(
    excludeGroups.length > 0 || excludePeople.length > 0,
  );
  const [specificModeSelected, setSpecificModeSelected] = useState(
    includeScope.includes('groups') || includeScope.includes('individuals'),
  );
  const everyoneSelected = includeScope.includes('everyone');
  const specificSelected =
    !everyoneSelected &&
    (specificModeSelected ||
      includeScope.includes('groups') ||
      includeScope.includes('individuals'));
  const groupAudienceOptions = useMemo(
    () => groupOptions.map((option) => ({...option, kind: 'group' as const})),
    [groupOptions],
  );
  const peopleAudienceOptions = useMemo(
    () => peopleOptions.map((option) => ({...option, kind: 'person' as const})),
    [peopleOptions],
  );
  const selectedGroups = useMemo(
    () => includeGroups.map((option) => ({...option, kind: 'group' as const})),
    [includeGroups],
  );
  const selectedPeople = useMemo(
    () => includePeople.map((option) => ({...option, kind: 'person' as const})),
    [includePeople],
  );
  const selectedExclusions = useMemo(
    () => [
      ...excludeGroups.map((option) => ({...option, kind: 'group' as const})),
      ...excludePeople.map((option) => ({...option, kind: 'person' as const})),
    ],
    [excludeGroups, excludePeople],
  );

  const toggleIncludeScope = (value: IncludeScopeOption) => {
    const specificScopes = includeScope.filter(
      (scope) => scope !== 'everyone',
    ) as IncludeScope;
    onIncludeScopeChange(
      specificScopes.includes(value)
        ? (specificScopes.filter((scope) => scope !== value) as IncludeScope)
        : ([...specificScopes, value] as IncludeScope),
    );
  };

  const selectEveryone = () => {
    setSpecificModeSelected(false);
    onIncludeScopeChange(['everyone']);
  };

  const selectSpecific = () => {
    setSpecificModeSelected(true);
    if (everyoneSelected) onIncludeScopeChange([]);
  };

  const updateExclusions = (options: AudienceOption[]) => {
    const groups = options
      .filter((option) => option.kind === 'group')
      .map(({label, value}) => ({label, value}));
    const people = options
      .filter((option) => option.kind === 'person')
      .map(({label, value}) => ({label, value}));

    onExcludeGroupsChange(groups);
    onExcludePeopleChange(people);
    onExcludeScopeChange([
      ...(groups.length > 0 ? (['groups'] as const) : []),
      ...(people.length > 0 ? (['individuals'] as const) : []),
    ]);
  };

  const clearExclusions = () => {
    updateExclusions([]);
    setShowExclusions(false);
  };

  return (
    <div className={`flex w-full flex-col ${compact ? 'gap-4' : 'gap-6'}`}>
      <CheckInNameSection
        compact={compact}
        value={cycleName}
        onChange={onCycleNameChange}
      />

      <section className="rounded-[10px] border border-borderLight bg-white p-4">
        <Body2
          id="checkin-audience-label"
          className="leading-[22px]"
          kind="blackTwo"
          weight="bold">
          Send to
        </Body2>
        <Body2 className="mb-2 mt-1 leading-[22px]" kind="textBody">
          Choose everyone, or select groups and people to include.
        </Body2>

        {audienceError && (
          <div className="mb-2 flex items-center justify-between rounded-lg bg-red100 px-3 py-2">
            <Body2 kind="red400">{audienceError}</Body2>
            {onRetryAudience && (
              <button
                type="button"
                onClick={onRetryAudience}
                className="text-sm font-semibold leading-[22px] text-red400 underline">
                Retry
              </button>
            )}
          </div>
        )}

        <div
          className="flex flex-col gap-2"
          role="group"
          aria-labelledby="checkin-audience-label">
          <RecipientOption
            compact={compact}
            label="Everyone"
            description="The entire company"
            selectionType="radio"
            name="checkin-audience-mode"
            selected={everyoneSelected}
            onSelect={selectEveryone}
          />
          <RecipientOption
            compact={compact}
            label="Specific groups or people"
            description="Choose groups, individuals, or both"
            selectionType="radio"
            name="checkin-audience-mode"
            selected={specificSelected}
            onSelect={selectSpecific}>
            <div className="flex flex-col gap-2">
              <RecipientOption
                compact
                label="Groups or teams"
                description="Choose one or more groups"
                selectionType="checkbox"
                selected={includeScope.includes('groups')}
                onSelect={() => toggleIncludeScope('groups')}>
                <AudiencePicker
                  compact={compact}
                  id="included-groups"
                  options={groupAudienceOptions}
                  selected={selectedGroups}
                  placeholder="Search groups or teams"
                  emptyLabel="No groups or teams found"
                  isLoading={isLoadingGroups}
                  onChange={(options) =>
                    onIncludeGroupsChange(
                      options.map(({label, value}) => ({label, value})),
                    )
                  }
                />
              </RecipientOption>
              <RecipientOption
                compact
                label="Individuals"
                description="Choose people by name"
                selectionType="checkbox"
                selected={includeScope.includes('individuals')}
                onSelect={() => toggleIncludeScope('individuals')}>
                <AudiencePicker
                  compact={compact}
                  id="included-people"
                  options={peopleAudienceOptions}
                  selected={selectedPeople}
                  placeholder="Search by name..."
                  emptyLabel="No people found"
                  onChange={(options) =>
                    onIncludePeopleChange(
                      options.map(({label, value}) => ({label, value})),
                    )
                  }
                />
              </RecipientOption>
            </div>
          </RecipientOption>
        </div>
      </section>

      {includeScope.length > 0 &&
        (showExclusions ||
        excludeGroups.length > 0 ||
        excludePeople.length > 0 ? (
          <section className="rounded-[10px] border border-red200 bg-red100 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Body1 className="leading-6" kind="blackTwo" weight="bold">
                  Specific groups, teams, or people
                </Body1>
                <Body2 className="mt-1 leading-[22px]" kind="textBody">
                  These selections won&apos;t receive this check-in.
                </Body2>
              </div>
              <button
                type="button"
                onClick={clearExclusions}
                className="shrink-0 text-sm font-semibold leading-[22px] text-red400 underline focus:outline-none focus:ring-2 focus:ring-red200">
                Remove exclusions
              </button>
            </div>
            <div className="mt-4">
              <AudiencePicker
                compact={compact}
                id="excluded-audience"
                options={[...groupAudienceOptions, ...peopleAudienceOptions]}
                selected={selectedExclusions}
                placeholder="Search groups or individuals to exclude..."
                emptyLabel="No groups or people found"
                isLoading={
                  isLoadingGroups && peopleAudienceOptions.length === 0
                }
                groupByKind
                onChange={updateExclusions}
              />
            </div>
          </section>
        ) : (
          <button
            type="button"
            onClick={() => setShowExclusions(true)}
            className="w-fit rounded-lg px-4 py-3.5 text-left text-sm font-medium leading-[22px] text-red400 focus:outline-none focus:ring-2 focus:ring-red200">
            ⛔ Exclude someone from this check-in
          </button>
        ))}
    </div>
  );
};
